import React, { useState, useMemo } from "react";
import { ClusterCenter, KeywordWithMetadata } from "../types";
import { formatCenterTrendValue } from "../utils/trendCellDisplay";
import { isCenterPlottable } from "../utils/clusterCenter";
import { useAuth } from "../contexts/AuthContext";
import TopicSuggestionsModal from "./TopicSuggestionsModal";
import SerpPreviewModal from "./SerpPreviewModal";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import HistoryIcon from "@mui/icons-material/History";
import Tooltip from "@mui/material/Tooltip";
import "./KeywordsTable.css";

// Hint for the stale-rank marker. A ranked keyword shows its last known rank;
// an unranked one ("N/A") has no recent successful check to go by.
export const staleRankHint = (ranking: number): string =>
  ranking
    ? "Last known ranking. It couldn't be refreshed recently, so it may be out of date."
    : "Ranking couldn't be checked recently, so it may be out of date.";

interface KeywordsTableProps {
  keywords: KeywordWithMetadata[];
  selectedAnchor: string;
  clientId: string;
  isLoading: boolean;
  locationCode?: number;
  languageCode?: string;
  // Volume-weighted cluster center; rendered as a pinned bottom row. Null = none.
  center?: ClusterCenter | null;
}

type SortField = "keyword" | "volume" | "difficulty" | "ranking" | "relevance";
type SortDirection = "asc" | "desc";

const KeywordsTable: React.FC<KeywordsTableProps> = ({
  keywords,
  selectedAnchor,
  clientId,
  isLoading,
  locationCode = 2840,
  languageCode = "en",
  center = null,
}) => {
  const { isAuthenticated, tenantFeatures, user } = useAuth();
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [sortField, setSortField] = useState<SortField>("volume");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Action menu states
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);

  // Modal states
  const [isTopicSuggestionsModalOpen, setIsTopicSuggestionsModalOpen] =
    useState<boolean>(false);
  const [selectedKeywordForSuggestions, setSelectedKeywordForSuggestions] =
    useState<string>("");
  const [isSerpPreviewModalOpen, setIsSerpPreviewModalOpen] =
    useState<boolean>(false);
  const [selectedKeywordForSerp, setSelectedKeywordForSerp] =
    useState<string>("");

  // Tooltip state for ranking URLs
  const [hoveredUrlIndex, setHoveredUrlIndex] = useState<number | null>(null);

  // Create a copy of the data for independent sorting
  const sortedKeywords = useMemo(() => {
    if (!keywords || keywords.length === 0) return [];

    const keywordsCopy = [...keywords];

    return keywordsCopy.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case "keyword":
          aValue = a.keyword.toLowerCase();
          bValue = b.keyword.toLowerCase();
          break;
        case "volume":
          aValue = a.volume || 0;
          bValue = b.volume || 0;
          break;
        case "difficulty":
          aValue = a.difficulty || 0;
          bValue = b.difficulty || 0;
          break;
        case "ranking":
          aValue = a.ranking || Infinity;
          bValue = b.ranking || Infinity;
          break;
        case "relevance":
          aValue = a.openai_similarity ?? a.relevance_score ?? 0;
          bValue = b.openai_similarity ?? b.relevance_score ?? 0;
          break;
        default:
          return 0;
      }

      if (sortDirection === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [keywords, sortField, sortDirection]);

  // When every keyword is unranked, the volume-weighted ranking collapses to
  // the "unranked = 100" sentinel. Showing a hard 100 next to a column of N/As
  // reads as "the cluster ranks at position 100", so we display N/A instead
  // (the diamond's size still uses the computed 100 internally).
  const allKeywordsUnranked = useMemo(
    () => keywords.length > 0 && keywords.every((k) => !k.ranking),
    [keywords]
  );

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return (
        <UnfoldMoreIcon
          style={{ fontSize: 16, marginLeft: 4, opacity: 0.6 }}
        />
      );
    }

    if (sortDirection === "asc") {
      return (
        <ArrowUpwardIcon
          style={{ fontSize: 16, marginLeft: 4 }}
        />
      );
    } else {
      return (
        <ArrowDownwardIcon
          style={{ fontSize: 16, marginLeft: 4 }}
        />
      );
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Handle action menu
  const handleActionMenuToggle = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuIndex(openMenuIndex === index ? null : index);
  };

  const handleTopicSuggestions = (keyword: string) => {
    setSelectedKeywordForSuggestions(keyword);
    setIsTopicSuggestionsModalOpen(true);
    setOpenMenuIndex(null);
  };

  const handleSerpPreview = (keyword: string) => {
    setSelectedKeywordForSerp(keyword);
    setIsSerpPreviewModalOpen(true);
    setOpenMenuIndex(null);
  };

  const closeTopicSuggestionsModal = () => {
    setIsTopicSuggestionsModalOpen(false);
    setSelectedKeywordForSuggestions("");
  };

  const closeSerpPreviewModal = () => {
    setIsSerpPreviewModalOpen(false);
    setSelectedKeywordForSerp("");
  };

  // CSV Export functionality
  const exportToCSV = () => {
    const csvHeaders = [
      "Keyword",
      "Volume",
      "Difficulty",
      "Ranking",
      "Ranking Stale",
      "Relevance (%)",
      "Ranking URL",
      "Trend Week",
      "Trend Month",
      "Trend Life",
    ];

    const csvData = sortedKeywords.map((keyword) => {
      const similarity =
        keyword.openai_similarity ?? keyword.relevance_score;
      return [
        keyword.keyword,
        keyword.volume ?? "N/A",
        keyword.difficulty ?? "N/A",
        keyword.ranking || "N/A",
        keyword.rank_is_stale ? "Yes" : "No",
        similarity != null ? (similarity * 100).toFixed(1) : "N/A",
        keyword.ranking_url || "N/A",
        keyword.trend?.week ?? "N/A",
        keyword.trend?.month ?? "N/A",
        keyword.trend?.life ?? "N/A",
      ];
    });

    // Append the computed center as a final row. Per spec, Volume is blank
    // ("--", it's the weight, not a value) and Ranking URL is a truly empty
    // cell (per ticket); Difficulty, Ranking and Trend carry the
    // weighted/averaged values.
    if (
      center &&
      center.difficulty != null &&
      center.ranking != null
    ) {
      csvData.push([
        "Cluster Weighted Average",
        "--",
        Math.round(center.difficulty),
        allKeywordsUnranked ? "N/A" : center.ranking,
        "--",
        "--",
        "",
        center.trend?.week != null
          ? formatCenterTrendValue(center.trend.week)
          : "N/A",
        center.trend?.month != null
          ? formatCenterTrendValue(center.trend.month)
          : "N/A",
        center.trend?.life != null
          ? formatCenterTrendValue(center.trend.life)
          : "N/A",
      ]);
      // Note below the center row (mirrors the on-screen caption), spanning the
      // Keyword column with the rest blank to keep the columns aligned.
      csvData.push([
        "Weighted by volume — a computed cluster overview, not a real keyword.",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
      ]);
    }

    const csvContent = [csvHeaders, ...csvData]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    // Prepend a UTF-8 BOM so Excel decodes the file as UTF-8 instead of the
    // system locale (Windows-1252) — otherwise non-ASCII characters like the
    // em-dash in the center-note row render as mojibake ("â€"").
    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);

      const filename = selectedAnchor
        ? `keywords_${selectedAnchor
            .replace(/[^a-z0-9]/gi, "_")
            .toLowerCase()}_${new Date().toISOString().split("T")[0]}.csv`
        : `keywords_${new Date().toISOString().split("T")[0]}.csv`;

      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Close action menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuIndex(null);
    };

    if (openMenuIndex !== null) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [openMenuIndex]);

  if (isLoading) {
    return (
      <div className="keywords-table-container">
        <div className="keywords-table-header">
          <h3>Keywords Data</h3>
          <div className="loading-indicator">Loading...</div>
        </div>
      </div>
    );
  }

  if (!keywords || keywords.length === 0) {
    return (
      <div className="keywords-table-container">
        <div className="keywords-table-header">
          <h3>Keywords Data</h3>
          <div className="empty-state">No keywords available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="keywords-table-container">
      <div className="keywords-table-header" onClick={toggleExpanded}>
        <div className="table-header-content">
          <h3>
            Keywords Data
            {selectedAnchor && (
              <span className="cluster-name">for "{selectedAnchor}"</span>
            )}
          </h3>
          <div className="table-summary">
            {keywords.length} keyword{keywords.length !== 1 ? "s" : ""}
          </div>
        </div>
        <div className="table-header-actions">
          {isExpanded && (
            <button
              className="export-button"
              onClick={(e) => {
                e.stopPropagation();
                exportToCSV();
              }}
              title="Export to CSV"
            >
              <FileDownloadIcon style={{ fontSize: 18 }} />
              Export CSV
            </button>
          )}
          <button
            className="expand-button"
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            <ExpandMoreIcon
              className={`expand-icon ${isExpanded ? "expanded" : ""}`}
              style={{ fontSize: 20, color: "#2d3748" }}
            />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="keywords-table-content">
          <div className="keywords-table">
            <div
              className={`table-header ${
                isAuthenticated ? "with-actions" : ""
              }`}
            >
              <div
                className={`table-col keyword-col sortable ${
                  sortField === "keyword" ? "sorted" : ""
                }`}
                onClick={() => handleSort("keyword")}
              >
                <span>Keyword</span>
                {getSortIcon("keyword")}
              </div>
              <div
                className={`table-col volume-col sortable ${
                  sortField === "volume" ? "sorted" : ""
                }`}
                onClick={() => handleSort("volume")}
              >
                <span>Volume</span>
                {getSortIcon("volume")}
              </div>
              <div
                className={`table-col difficulty-col sortable ${
                  sortField === "difficulty" ? "sorted" : ""
                }`}
                onClick={() => handleSort("difficulty")}
              >
                <span>Difficulty</span>
                {getSortIcon("difficulty")}
              </div>
              <div
                className={`table-col ranking-col sortable ${
                  sortField === "ranking" ? "sorted" : ""
                }`}
                onClick={() => handleSort("ranking")}
              >
                <span>Ranking</span>
                {getSortIcon("ranking")}
              </div>
              <div
                className={`table-col relevance-col sortable ${
                  sortField === "relevance" ? "sorted" : ""
                }`}
                onClick={() => handleSort("relevance")}
              >
                <span>Relevance</span>
                {getSortIcon("relevance")}
              </div>
              {isAuthenticated && (
                <div className="table-col actions-col">
                  <span></span>
                </div>
              )}
            </div>

            <div className="table-body">
              {sortedKeywords.map((keyword, index) => (
                <div
                  key={`${keyword.keyword}-${index}`}
                  className={`table-row ${
                    keyword.isAnchor ? "anchor-keyword" : ""
                  }`}
                >
                  <div className="table-col keyword-col">
                    <span className="keyword-text">
                      {keyword.keyword}
                      {keyword.isAnchor && (
                        <span className="anchor-badge">Anchor</span>
                      )}
                      {keyword.ranking_url &&
                        keyword.ranking_url !== "NOT FOUND" && (
                          <span
                            className="ranking-url-info"
                            onMouseEnter={() => setHoveredUrlIndex(index)}
                            onMouseLeave={() => setHoveredUrlIndex(null)}
                            onClick={() =>
                              window.open(
                                keyword.ranking_url,
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                          >
                            <InfoIcon style={{ fontSize: 16, opacity: 0.7 }} />
                            {hoveredUrlIndex === index && (
                              <div className="ranking-url-tooltip">
                                <div
                                  style={{
                                    fontWeight: "bold",
                                    marginBottom: "4px",
                                  }}
                                >
                                  Ranking URL:
                                </div>
                                <div style={{ textDecoration: "underline" }}>
                                  {keyword.ranking_url.length > 100
                                    ? `${keyword.ranking_url.substring(
                                        0,
                                        100
                                      )}...`
                                    : keyword.ranking_url}
                                </div>
                              </div>
                            )}
                          </span>
                        )}
                    </span>
                  </div>
                  <div className="table-col volume-col">
                    {keyword.volume != null
                      ? keyword.volume.toLocaleString()
                      : "N/A"}
                  </div>
                  <div className="table-col difficulty-col">
                    {keyword.difficulty ?? "N/A"}
                  </div>
                  <div className="table-col ranking-col">
                    {keyword.ranking || "N/A"}
                    {keyword.rank_is_stale && (
                      <Tooltip title={staleRankHint(keyword.ranking)} arrow>
                        <span
                          className="rank-stale-indicator"
                          role="img"
                          aria-label={staleRankHint(keyword.ranking)}
                          tabIndex={0}
                        >
                          <HistoryIcon aria-hidden="true" style={{ fontSize: 16 }} />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  <div className="table-col relevance-col">
                    {(keyword.openai_similarity ?? keyword.relevance_score) !=
                    null
                      ? (
                          ((keyword.openai_similarity ??
                            keyword.relevance_score) as number) * 100
                        ).toFixed(1) + "%"
                      : "N/A"}
                  </div>
                  {isAuthenticated && (
                    <div className="table-col actions-col">
                      <div className="dropdown-menu-container">
                        <button
                          className="action-menu-btn"
                          onClick={(e) => handleActionMenuToggle(index, e)}
                          title="Actions"
                          disabled={isLoading}
                        >
                          ⋯
                        </button>

                        {openMenuIndex === index && (
                          <>
                            <div
                              className="menu-backdrop"
                              onClick={() => setOpenMenuIndex(null)}
                            ></div>
                            <div className="dropdown-menu">
                              <button
                                className="dropdown-item"
                                onClick={() =>
                                  handleTopicSuggestions(keyword.keyword)
                                }
                              >
                                Topic Suggestions
                              </button>
                              {(tenantFeatures.serp_preview_enabled || user?.is_superadmin) && (
                                <button
                                  className="dropdown-item"
                                  onClick={() =>
                                    handleSerpPreview(keyword.keyword)
                                  }
                                >
                                  SERP Preview
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Pinned computed-center row (always at the bottom, unaffected by
                  sorting). Column headers are unchanged; the values are the
                  volume-weighted/averaged center. Ranking URL and Relevance have
                  no meaning for a computed marker, so they show a dash. */}
              {isCenterPlottable(center) && (
                  <div
                    className="table-row center-row"
                      style={{
                        borderTop: "2px solid #e2e8f0",
                        borderLeft: "4px solid #7c5cfc",
                        backgroundColor: "#f1eefe",
                        fontWeight: 700,
                        color: "#2d3748",
                      }}
                    >
                      <div
                        className="table-col keyword-col"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <span className="keyword-text">
                          Cluster Weighted Average
                        </span>
                        <span
                          style={{
                            marginTop: "2px",
                            fontSize: "12px",
                            fontStyle: "italic",
                            fontWeight: 400,
                            color: "#6b6880",
                          }}
                        >
                          Weighted by volume — a computed cluster overview, not a
                          real keyword.
                        </span>
                      </div>
                      <div className="table-col volume-col">—</div>
                      <div className="table-col difficulty-col">
                        {Math.round(center.difficulty)}
                      </div>
                      <div className="table-col ranking-col">
                        {allKeywordsUnranked ? "N/A" : center.ranking}
                      </div>
                      <div className="table-col relevance-col">—</div>
                      {isAuthenticated && (
                        <div className="table-col actions-col">
                          <span></span>
                        </div>
                      )}
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Topic Suggestions Modal */}
      <TopicSuggestionsModal
        isOpen={isTopicSuggestionsModalOpen}
        onClose={closeTopicSuggestionsModal}
        keyword={selectedKeywordForSuggestions}
      />

      {/* SERP Preview Modal */}
      <SerpPreviewModal
        isOpen={isSerpPreviewModalOpen}
        onClose={closeSerpPreviewModal}
        keyword={selectedKeywordForSerp}
        anchorKeyword={selectedAnchor}
        clientId={clientId}
        locationCode={locationCode}
        languageCode={languageCode}
      />
    </div>
  );
};

export default KeywordsTable;
